import { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs, startAfter } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Box from '@mui/material/Box';
import { db } from '../../firebase';

const PAGE_SIZE = 100;

const cellStyle = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  letterSpacing: '.08em',
  color: 'var(--c-ink-2)',
  borderBottom: '1px solid var(--c-line-soft)',
  padding: '10px 14px',
  fontVariantNumeric: 'tabular-nums slashed-zero',
};

const headCellStyle = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '.16em',
  fontWeight: 600,
  color: 'var(--c-ink-3)',
  textTransform: 'uppercase',
  borderBottom: '1px solid var(--c-line-hard)',
  padding: '10px 14px',
  background: 'var(--c-bg)',
};

const FILTERS = ['ALL', 'THIS WEEK', 'SUSPICIOUS ONLY', 'MOCKED ONLY', 'SILENT DUPLICATES'];

const startOfWeek = () => {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
};

const formatDateTime = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getDate()}/${d.getMonth() + 1} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const Stars = ({ n }) => {
  const count = Number(n) || 0;
  return (
    <span style={{ color: 'var(--c-brand)', letterSpacing: 1 }}>
      {'★'.repeat(count)}{'☆'.repeat(Math.max(0, 5 - count))}
    </span>
  );
};

const FlagBadge = ({ label, color, bg }) => (
  <span style={{
    fontFamily: 'var(--font-mono)',
    fontSize: 9,
    letterSpacing: '.12em',
    fontWeight: 700,
    color,
    background: bg,
    padding: '2px 5px',
    marginRight: 3,
    display: 'inline-block',
  }}>{label}</span>
);

const FeedbackAdminList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [activeFilter, setActiveFilter] = useState('ALL');

  const weekStart = startOfWeek();

  const fetchRows = async (afterCursor = null) => {
    setLoading(true);
    try {
      let q = query(
        collection(db, 'feedback_collection'),
        orderBy('feedback_collection_DateTime', 'desc'),
        limit(PAGE_SIZE + 1),
      );
      if (afterCursor) q = query(q, startAfter(afterCursor));
      const snap = await getDocs(q);
      const docs = snap.docs.slice(0, PAGE_SIZE).map((d) => ({ id: d.id, ...d.data() }));
      setHasMore(snap.docs.length > PAGE_SIZE);
      setCursor(snap.docs[PAGE_SIZE - 1] ?? null);
      if (afterCursor) {
        setRows((prev) => [...prev, ...docs]);
      } else {
        setRows(docs);
      }
    } catch (err) {
      console.error('FeedbackAdminList fetch error:', err);
      setError(err.message ?? 'unknown error');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); }, []);

  const filtered = rows.filter((r) => {
    // Silent duplicates are a fraud trap — hide from all default views, show
    // ONLY when the SILENT DUPLICATES chip is active so admins can audit.
    if (activeFilter === 'SILENT DUPLICATES') {
      return r.silent_duplicate === true;
    }
    if (r.silent_duplicate === true) return false;

    if (activeFilter === 'THIS WEEK') {
      const dt = r.feedback_collection_DateTime?.toDate?.() ?? null;
      if (!dt || dt < weekStart) return false;
    }
    if (activeFilter === 'SUSPICIOUS ONLY') {
      const isSuspicious = r.flags?.includes?.('suspicious') ||
        Number(r.displacementOTPtoSubmitM || 0) > 500 ||
        r.flags?.includes?.('impossible_travel') ||
        r.flags?.includes?.('cluster_duplicate') ||
        r.flags?.includes?.('shop_name_duplicate') ||
        r.flags?.includes?.('device_shared');
      if (!isSuspicious) return false;
    }
    if (activeFilter === 'MOCKED ONLY') {
      if (!r.mockedAtAnyStage) return false;
    }
    return true;
  });

  return (
    <div className="db-page" style={{ minHeight: '100vh' }}>
      <div className="db-ribbon" style={{ borderBottom: '1px solid var(--c-line-soft)', padding: 'var(--sp-3) var(--pad-h)' }}>
        <div className="db-ribbon-title">FEEDBACK ADMIN</div>
        <div className="db-ribbon-actions">
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.10em', color: 'var(--c-ink-3)' }}>
            {loading ? 'LOADING…' : `${filtered.length} RECORDS`}
          </span>
        </div>
      </div>

      {/* Filter chips */}
      <Box sx={{ display: 'flex', gap: 'var(--sp-2)', padding: 'var(--sp-3) var(--pad-h)', borderBottom: '1px solid var(--c-line-soft)', flexWrap: 'wrap' }}>
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setActiveFilter(f)}
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '.12em',
              fontWeight: activeFilter === f ? 700 : 400,
              color: activeFilter === f ? 'var(--c-brand)' : 'var(--c-ink-3)',
              background: activeFilter === f ? 'rgba(234,88,12,.08)' : 'transparent',
              border: activeFilter === f ? '1px solid var(--c-brand)' : '1px solid var(--c-line-soft)',
              padding: '4px 10px',
              cursor: 'pointer',
            }}
          >
            {f}
          </button>
        ))}
      </Box>

      <section style={{ padding: 'var(--sp-4) var(--pad-h) var(--sp-12)', overflowX: 'auto' }}>
        {error && !loading && (
          <div style={{ padding: 'var(--sp-8) 0', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.12em', color: 'var(--c-danger)' }}>
            FAILED TO LOAD — {error}
          </div>
        )}
        {loading && rows.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.12em', color: 'var(--c-ink-3)', padding: 'var(--sp-8) 0' }}>
            <div className="spinner" /> LOADING…
          </div>
        ) : (
          <>
            <div style={{ border: '1px solid var(--c-line-soft)', background: 'var(--c-bg)' }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    {['WHEN', 'SHOP', 'OWNER', 'COLLECTOR', 'STARS', 'GEOFENCE', 'FLAGS', ''].map((h) => (
                      <TableCell key={h} sx={headCellStyle}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ ...cellStyle, textAlign: 'center', padding: 'var(--sp-8) 0', color: 'var(--c-ink-3)' }}>
                        NO RECORDS
                      </TableCell>
                    </TableRow>
                  ) : filtered.map((r) => {
                    const isSuspicious = r.flags?.includes?.('suspicious') || Number(r.displacementOTPtoSubmitM || 0) > 500;
                    const isMocked = !!r.mockedAtAnyStage;
                    const geofence = r.resolvedSiteId
                      ? `${r.resolvedSiteId} · ${Math.round(r.resolvedSiteDistanceM ?? 0)}m`
                      : '—';
                    return (
                      <TableRow
                        key={r.id}
                        onClick={() => navigate(`/feedback/admin/${r.id}`)}
                        sx={{ cursor: 'pointer', '&:hover': { background: 'var(--c-bg-2)' } }}
                      >
                        <TableCell sx={cellStyle}>{formatDateTime(r.feedback_collection_DateTime)}</TableCell>
                        <TableCell sx={{ ...cellStyle, color: 'var(--c-ink)', fontFamily: "'Geist', sans-serif", fontSize: 12, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.shop_name || '—'}
                        </TableCell>
                        <TableCell sx={{ ...cellStyle, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.shop_owner_name || '—'}
                        </TableCell>
                        <TableCell sx={{ ...cellStyle, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.feedback_collector_name || '—'}
                        </TableCell>
                        <TableCell sx={cellStyle}><Stars n={r.feedback_stars} /></TableCell>
                        <TableCell sx={cellStyle}>{geofence}</TableCell>
                        <TableCell sx={cellStyle}>
                          {r.silent_duplicate === true && <FlagBadge label="👻 SILENT DUP" color="#7c3aed" bg="rgba(124,58,237,.10)" />}
                          {isSuspicious && <FlagBadge label="🚩 SUSPICIOUS" color="#dc2626" bg="rgba(220,38,38,.10)" />}
                          {isMocked && <FlagBadge label="🎭 MOCKED" color="var(--c-ink-3)" bg="var(--c-bg-2)" />}
                          {r.flags?.includes?.('impossible_travel') && <FlagBadge label="✈️ IMPOSSIBLE TRAVEL" color="#dc2626" bg="rgba(220,38,38,.10)" />}
                          {r.flags?.includes?.('cluster_duplicate') && <FlagBadge label="📍 CLUSTER" color="#d97706" bg="rgba(217,119,6,.10)" />}
                          {r.flags?.includes?.('shop_name_duplicate') && <FlagBadge label="🏪 SHOP DUP" color="#d97706" bg="rgba(217,119,6,.10)" />}
                          {r.flags?.includes?.('image_duplicate') && <FlagBadge label="🖼 IMG DUP" color="#d97706" bg="rgba(217,119,6,.10)" />}
                          {r.flags?.includes?.('device_shared') && <FlagBadge label="📱 SHARED DEVICE" color="#d97706" bg="rgba(217,119,6,.10)" />}
                        </TableCell>
                        <TableCell sx={{ ...cellStyle, padding: '10px 14px' }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/feedback/admin/${r.id}`); }}
                            className="db-pill"
                            style={{ fontSize: 10, padding: '4px 10px', letterSpacing: '.10em', cursor: 'pointer' }}
                          >
                            📍 PIN
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {hasMore && (
              <div style={{ padding: 'var(--sp-4) 0', display: 'flex', justifyContent: 'center' }}>
                <button
                  onClick={() => fetchRows(cursor)}
                  disabled={loading}
                  className="db-pill"
                  style={{ fontSize: 11, padding: '8px 20px', letterSpacing: '.10em', cursor: loading ? 'wait' : 'pointer' }}
                >
                  {loading ? 'LOADING…' : 'LOAD MORE'}
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default FeedbackAdminList;
